import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, computed, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Title, Meta } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';
import { map, take } from 'rxjs';
import { AuthFestiveDecoration } from '@app/shared/components/auth-festive-decoration/auth-festive-decoration';
import { FestivalDecoration, FestivalType, getActiveFestivalType, shouldHideLoginIllustrations } from '@app/shared/components/festival-decoration/festival-decoration';
import { Spinner } from '@app/shared/components/spinner/spinner';
import { AuthService } from '@app/core/services/Auth/auth-service';
import { NavigationService } from '@app/core/services/sidebar/sidebar.service';
import { ThemeService } from '@app/core/services/theme/theme.service';
import { TimesheetStatusService } from '@app/core/services/timesheet-status/timesheet-status.service';
import { ValidationConstants } from '@app/constants/validation.constants';

const OTP_LENGTH = 4;

@Component({
  selector: 'app-login-with-otp',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    RouterModule,
    AuthFestiveDecoration,
    FestivalDecoration,
    Spinner,
  ],
  templateUrl: './login-with-otp.html',
  styleUrl: './login-with-otp.scss',
})
export class LoginWithOtp implements OnInit, OnDestroy {
  private readonly _isLoading = signal(false);
  private readonly _errorMessage = signal('');
  private readonly _showOtpField = signal(false);
  private readonly _entryPoint = signal('left');
  private readonly _contact = signal('');
  private readonly _isEmailTyping = signal(false);
  private readonly _isOtpTyping = signal(false);
  private loginScrollLocked = false;

  readonly isLoading = computed(() => this._isLoading());
  readonly errorMessage = computed(() => this._errorMessage());
  readonly showOtpField = computed(() => this._showOtpField());
  readonly entryPoint = computed(() => this._entryPoint());
  readonly contact = computed(() => this._contact());
  readonly isEmailTyping = computed(() => this._isEmailTyping());
  readonly isOtpTyping = computed(() => this._isOtpTyping());
  readonly otpIndexes = Array.from({ length: OTP_LENGTH }, (_, i) => i);
  readonly currentYear = new Date().getFullYear();
  /** Hide bike/moon illustrations during event windows that replace the login background. */
  readonly hideLoginIllustrations = shouldHideLoginIllustrations();
  private readonly activeFestival = getActiveFestivalType();
  readonly fallOfSummer = this.activeFestival === FestivalType.AUTUMN;
  readonly thanksgiving = this.activeFestival === FestivalType.THANKSGIVING;
  readonly halloween = this.activeFestival === FestivalType.HALLOWEEN;
  readonly remembrance = this.activeFestival === FestivalType.REMEMBRANCE;
  readonly loginHoneybees = [
    { left: '6%', top: '64%', size: '58px', duration: '7.2s', delay: '0s', path: 'down' },
    { left: '22%', top: '74%', size: '46px', duration: '8s', delay: '0.8s', path: 'right' },
    { left: '40%', top: '68%', size: '52px', duration: '6.6s', delay: '1.4s', path: 'left' },
  ] as const;
  shouldHideLogin = false;

  readonly stepTitle = computed(() =>
    this.showOtpField() ? 'Enter verification code' : 'Login with OTP'
  );

  readonly stepMessage = computed(() => {
    if (this.showOtpField()) {
      return `We sent a ${OTP_LENGTH}-digit code to ${this.maskContact(this.contact())}. Enter it below to sign in.`;
    }
    return 'Enter your email or phone number and we’ll send you a one-time login code.';
  });

  contactForm!: FormGroup;
  otpForm!: FormGroup;

  constructor(
    private readonly fb: FormBuilder,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly titleService: Title,
    private readonly metaService: Meta,
    private readonly toastr: ToastrService,
    private readonly themeService: ThemeService,
    private readonly navigationService: NavigationService,
    private readonly timesheetStatusService: TimesheetStatusService
  ) {
    this.initializeForms();
  }

  ngOnInit(): void {
    const entryPoints = ['left', 'right', 'top', 'bottom'] as const;
    this._entryPoint.set(entryPoints[Math.floor(Math.random() * entryPoints.length)]);

    this.route.data.subscribe((data: { [key: string]: unknown }) => {
      if (typeof data['title'] === 'string') {
        this.titleService.setTitle(data['title']);
      }
      if (typeof data['description'] === 'string') {
        this.metaService.updateTag({
          name: 'description',
          content: data['description'],
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.unlockLoginScroll();
  }

  private initializeForms(): void {
    this.contactForm = this.fb.group({
      email: ['', [Validators.required, ValidationConstants.emailOrPhoneValidator()]],
    });

    const otpControls: Record<string, unknown> = {};
    for (let i = 1; i <= OTP_LENGTH; i++) {
      otpControls[`otp${i}`] = ['', [Validators.required, Validators.pattern('^[0-9]$')]];
    }
    this.otpForm = this.fb.group(otpControls);
  }

  get email() {
    return this.contactForm.get('email');
  }

  showEmailFieldError(): boolean {
    return ValidationConstants.shouldShowEmailOrPhoneFieldError(this.email, this.isEmailTyping());
  }

  getErrorMessage(controlName: string, formGroup: FormGroup): string {
    const control = formGroup.get(controlName);

    if (control?.errors?.['backendError']) {
      return control.errors['backendErrorMessage'] || 'An error occurred. Please try again.';
    }

    if (controlName === 'email') {
      if (control?.errors?.['required'] && control.touched) {
        return 'Please enter Email or Phone';
      }
      return ValidationConstants.getEmailOrPhoneErrorMessage(control!, this.isEmailTyping());
    }

    return '';
  }

  onEmailInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    if (this.errorMessage()) {
      this._errorMessage.set('');
    }
    this.clearControlBackendError(this.email);
    this._isEmailTyping.set(true);

    if (value.includes('@')) {
      const lowercaseValue = value.toLowerCase();
      if (value !== lowercaseValue) {
        input.value = lowercaseValue;
        this.email?.setValue(lowercaseValue, { emitEvent: false });
      }
    }
  }

  onEmailFocus(): void {
    if (this.errorMessage()) {
      this._errorMessage.set('');
    }
    this.clearControlBackendError(this.email);
  }

  onEmailBlur(): void {
    this._isEmailTyping.set(false);
  }

  onOtpInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/\D/g, '').slice(0, 1);
    if (input.value !== value) {
      input.value = value;
    }

    this.clearOtpBackendErrors();
    this._isOtpTyping.set(true);

    const otpControl = this.otpForm.get(`otp${index + 1}`);
    otpControl?.setValue(value, { emitEvent: false });

    if (value.length === 1 && index < OTP_LENGTH - 1) {
      const nextInput = input.nextElementSibling as HTMLInputElement | null;
      nextInput?.focus();
    }
  }

  onKeyDown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Backspace' && index > 0) {
      const input = event.target as HTMLInputElement;
      if (!input.value) {
        const prevInput = input.previousElementSibling as HTMLInputElement | null;
        prevInput?.focus();
      }
    }
  }

  onOtpPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text');
    if (!pastedData) {
      return;
    }

    const cleanData = pastedData.replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!cleanData.length) {
      return;
    }

    this.clearOtpBackendErrors();
    for (let i = 0; i < OTP_LENGTH; i++) {
      this.otpForm.get(`otp${i + 1}`)?.setValue(cleanData[i] ?? '');
    }

    const target = event.target as HTMLInputElement;
    const inputs = target.parentElement?.querySelectorAll(
      'input[type="text"]'
    ) as NodeListOf<HTMLInputElement> | undefined;
    if (inputs?.length) {
      const focusIndex = Math.min(cleanData.length, OTP_LENGTH) - 1;
      inputs[Math.max(focusIndex, 0)]?.focus();
    }
  }

  sendOtp(): void {
    if (!this.contactForm.valid) {
      this._isEmailTyping.set(false);
      this.email?.markAsTouched();
      return;
    }

    this._isLoading.set(true);
    this._errorMessage.set('');
    this._isEmailTyping.set(false);

    const contact = String(this.contactForm.get('email')?.value ?? '').trim();
    this._contact.set(contact);

    this.authService.sendOTP(contact).subscribe({
      next: () => {
        this._isLoading.set(false);
        this._errorMessage.set('');
        this._showOtpField.set(true);
        this.resetOtpForm();
        this.toastr.success('OTP sent successfully');
      },
      error: (error) => {
        this._isLoading.set(false);
        const errorMsg = error?.error?.message || 'Sending OTP failed. Please try again.';
        this._errorMessage.set(errorMsg);
        this.applyContactBackendError(error, errorMsg);
      },
    });
  }

  loginWithOtp(): void {
    if (!this.otpForm.valid) {
      Object.keys(this.otpForm.controls).forEach((key) => {
        this.otpForm.get(key)?.markAsTouched();
      });
      return;
    }

    this._isLoading.set(true);
    this._errorMessage.set('');
    this._isOtpTyping.set(false);

    const otp = Object.values(this.otpForm.value).join('');
    const contact = this._contact();

    this.authService.loginWithOtp(contact, otp).subscribe({
      next: (response: any) => {
        if (response?.data?.agencies?.length) {
          this._isLoading.set(false);
          this.toastr.info('Please complete sign-in by selecting an agency from the main login page.');
          void this.router.navigate(['/login']);
          return;
        }

        const userData = response?.data?.userData || response?.userData;
        if (userData) {
          this.beginLoginExitTransition();
          this.applyUserTheme(userData);
          this.navigationService.reloadNavigation();
          this.navigateAfterAuthentication(userData.redirectTo || '/dashboard');
          return;
        }

        this._isLoading.set(false);
        this._errorMessage.set(response?.message || 'Login failed. Please try again.');
        this.markOtpBackendError();
      },
      error: (error) => {
        this._isLoading.set(false);
        if (error?.status !== 401) {
          console.error('Login with OTP error:', error);
        }
        this._errorMessage.set(error?.error?.message || 'Login failed. Please try again.');
        this.markOtpBackendError();
      },
    });
  }

  changeContact(): void {
    this._showOtpField.set(false);
    this._errorMessage.set('');
    this.resetOtpForm();
  }

  maskContact(contact: string): string {
    const value = contact?.trim();
    if (!value) {
      return 'your registered contact';
    }

    if (value.includes('@')) {
      const [localPart, domain] = value.split('@');
      if (!localPart || !domain) {
        return value;
      }
      if (localPart.length <= 2) {
        return `${localPart[0] ?? '*'}*@${domain}`;
      }
      return `${localPart[0]}${'*'.repeat(Math.min(localPart.length - 2, 4))}${localPart[localPart.length - 1]}@${domain}`;
    }

    const digits = value.replace(/\D/g, '');
    if (digits.length >= 4) {
      return `***${digits.slice(-4)}`;
    }
    return value;
  }

  private applyContactBackendError(error: any, fallbackMessage: string): void {
    const emailControl = this.email;
    if (!emailControl) {
      return;
    }

    let message = fallbackMessage;
    if (Array.isArray(error?.error?.errors)) {
      const contactError = error.error.errors.find((err: any) => err.field === 'contact');
      if (contactError?.message) {
        message = contactError.message;
      }
    } else if (error?.error?.message) {
      message = error.error.message;
    }

    emailControl.setErrors({
      backendError: true,
      backendErrorMessage: message,
    });
    emailControl.markAsTouched();
  }

  private markOtpBackendError(): void {
    for (let i = 1; i <= OTP_LENGTH; i++) {
      const otpControl = this.otpForm.get(`otp${i}`);
      if (otpControl) {
        otpControl.setErrors({ backendError: true });
        otpControl.markAsTouched();
      }
    }
  }

  private clearOtpBackendErrors(): void {
    if (this.errorMessage()) {
      this._errorMessage.set('');
    }
    for (let i = 1; i <= OTP_LENGTH; i++) {
      const otpControl = this.otpForm.get(`otp${i}`);
      if (!otpControl?.errors?.['backendError']) {
        continue;
      }
      const { backendError, ...remainingErrors } = otpControl.errors;
      void backendError;
      otpControl.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
    }
  }

  private clearControlBackendError(control: ReturnType<FormGroup['get']>): void {
    if (!control?.errors?.['backendError']) {
      return;
    }
    const { backendError, backendErrorMessage, ...remainingErrors } = control.errors;
    void backendError;
    void backendErrorMessage;
    control.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
  }

  private resetOtpForm(): void {
    this.otpForm.reset();
    Object.keys(this.otpForm.controls).forEach((key) => {
      this.otpForm.get(key)?.setErrors(null);
    });
  }

  private applyUserTheme(userData: any): void {
    this.themeService.applyUserThemeFromData(userData);
  }

  private beginLoginExitTransition(): void {
    this.shouldHideLogin = true;
    this.lockLoginScroll();
  }

  private lockLoginScroll(): void {
    if (this.loginScrollLocked || typeof document === 'undefined') {
      return;
    }
    this.loginScrollLocked = true;
    window.scrollTo(0, 0);
    document.documentElement.classList.add('login-exit-scroll-lock');
    document.body.classList.add('login-exit-scroll-lock');
  }

  private unlockLoginScroll(): void {
    if (!this.loginScrollLocked || typeof document === 'undefined') {
      return;
    }
    this.loginScrollLocked = false;
    document.documentElement.classList.remove('login-exit-scroll-lock');
    document.body.classList.remove('login-exit-scroll-lock');
  }

  private isMobileLikeDevice(): boolean {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return false;
    }
    const mobileUa = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(
      navigator.userAgent
    );
    return mobileUa || window.innerWidth < 1199;
  }

  private navigateAfterAuthentication(defaultRoute: string): void {
    const forceDashboard = this.isMobileLikeDevice();
    if (forceDashboard) {
      this.authService.consumePostLoginRedirect();
    }

    this.timesheetStatusService
      .ensureStatus()
      .pipe(
        take(1),
        map((status) => {
          if (forceDashboard) {
            return '/dashboard';
          }
          const fallback = status.success ? defaultRoute : '/timesheets';
          return this.authService.getRedirectAfterLogin(fallback);
        })
      )
      .subscribe({
        next: (redirectPath) => {
          void this.router.navigateByUrl(redirectPath, { replaceUrl: true }).finally(() => {
            this._isLoading.set(false);
          });
        },
        error: () => {
          const redirectPath = forceDashboard
            ? '/dashboard'
            : this.authService.getRedirectAfterLogin(defaultRoute);
          void this.router.navigateByUrl(redirectPath, { replaceUrl: true }).finally(() => {
            this._isLoading.set(false);
          });
        },
      });
  }
}
